/**
 * TypographySettings — Reusable Typography Editor
 * ================================================
 *
 * Wraps the existing ProfileCustomizer to edit typography for any target:
 *   'user' | 'server' | 'category' | 'channel'
 *
 * One implementation. Multiple targets. No duplication.
 *
 * Usage:
 *   <TypographySettings
 *     targetType="server"
 *     targetId={serverId}
 *     initialCustomization={server.customization || {}}
 *     onSaved={(updated) => console.log('saved', updated)}
 *   />
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ProfileCustomizer from './ProfileCustomizer';
import {
  ProfileCustom,
  DEFAULT_CUSTOMIZATION,
} from '../core/identity/ProfileTypes';
import ProfilePreview from '../ProfilePreview';
import { supabase } from '../supabase';
import { generateMSNAvatar } from '../utils/uiUtils';
import { Hash, ChevronDown, Sparkles } from 'lucide-react';
import { getNameStyle, FONT_FAMILIES } from '../core/identity/styles';

export type TypographyTargetType = 'user' | 'server' | 'category' | 'channel';

interface TypographySettingsProps {
  targetType: TypographyTargetType;
  targetId: string;
  targetName?: string;
  targetAvatar?: string;
  initialCustomization?: Partial<ProfileCustom>;
  onSaved?: (customization: ProfileCustom) => void;
  onError?: (message: string) => void;
}

const TARGET_LABELS: Record<TypographyTargetType, string> = {
  user: 'User Profile',
  server: 'Server',
  category: 'Category',
  channel: 'Channel',
};

const TARGET_TABLE: Record<TypographyTargetType, string> = {
  user: 'profiles',
  server: 'vaults',
  category: 'categories',
  channel: 'channels',
};

export default function TypographySettings({
  targetType,
  targetId,
  targetName = 'Preview',
  targetAvatar,
  initialCustomization,
  onSaved,
  onError,
}: TypographySettingsProps) {
  // Memoize the baseline so it doesn't change identity every render
  const baselineCustomization = useMemo(
    () => ({ ...DEFAULT_CUSTOMIZATION, ...initialCustomization }),
    [JSON.stringify(initialCustomization)]
  );

  // Keep a ref to the latest baseline for handleReset
  const baselineRef = useRef(baselineCustomization);
  baselineRef.current = baselineCustomization;

  // Build a synthetic draft profile that ProfileCustomizer can consume
  const [draftProfile, setDraftProfile] = useState<any>(() => ({
    name: targetName,
    avatar: targetAvatar || generateMSNAvatar(targetId),
    status: 'Online',
    bio: '',
    bannerType: 'color',
    bannerValue: '#1e1e1e',
    gradientHue1: 260,
    gradientSat1: 80,
    gradientHue2: 320,
    gradientSat2: 80,
    bannerImage: '',
    profileBackgroundType: 'color',
    profileBackgroundValue: '#040711',
    profileBackgroundGradient: { hue1: 280, sat1: 90, hue2: 300, sat2: 90 },
    profileBackgroundImage: '',
    profileTextStyle: { fontSize: '14px', color: '#ffffff', fontFamily: 'Inter' },
    profileBorderStyle: { color: '#ffffff', gradient: 'none' },
    customization: baselineCustomization,
  }));

  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Sync initialCustomization into draft when it changes (and not dirty)
  useEffect(() => {
    if (!dirty) {
      setDraftProfile((prev: any) => ({
        ...prev,
        customization: baselineCustomization,
      }));
    }
  }, [baselineCustomization, dirty]);

  // Track dirty state
  useEffect(() => {
    setDirty(
      JSON.stringify(draftProfile.customization) !==
        JSON.stringify(baselineCustomization)
    );
  }, [draftProfile.customization, baselineCustomization]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      const table = TARGET_TABLE[targetType];
      if (!table) throw new Error(`Unknown target type: ${targetType}`);

      const { error } = await supabase
        .from(table)
        .update({ customization: draftProfile.customization })
        .eq('id', targetId);

      if (error) throw error;

      setDirty(false);
      onSaved?.(draftProfile.customization);
    } catch (err: any) {
      const msg = err?.message || 'Failed to save typography';
      setErrorMsg(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  }, [draftProfile.customization, targetType, targetId, onSaved, onError]);

  const handleBulkApply = useCallback(async () => {
    if (!window.confirm("Are you sure you want to overwrite typography for all child channels?")) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      // First save to current target
      const table = TARGET_TABLE[targetType];
      if (table) {
        await supabase
          .from(table)
          .update({ customization: draftProfile.customization })
          .eq('id', targetId);
      }
      
      // Then apply to children
      if (targetType === 'server') {
        const { error } = await supabase
          .from('channels')
          .update({ customization: draftProfile.customization })
          .eq('vault_id', targetId);
        if (error) throw error;
      } else if (targetType === 'category') {
        const { error } = await supabase
          .from('channels')
          .update({ customization: draftProfile.customization })
          .eq('category_id', targetId);
        if (error) throw error;
      }

      setDirty(false);
      onSaved?.(draftProfile.customization);
    } catch (err: any) {
      const msg = err?.message || 'Failed to bulk apply typography';
      setErrorMsg(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  }, [draftProfile.customization, targetType, targetId, onSaved, onError]);

  const handleRandomizeChannels = useCallback(async () => {
    if (!window.confirm("Are you sure you want to completely randomize typography for ALL channels? This cannot be undone!")) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const { data: channels, error: fetchError } = await supabase
        .from('channels')
        .select('id, customization')
        .eq(targetType === 'server' ? 'vault_id' : 'category_id', targetId);
      
      if (fetchError) throw fetchError;
      if (!channels || channels.length === 0) {
        alert("No channels found to randomize.");
        return;
      }
      
      const fonts = Object.keys(FONT_FAMILIES);
      const styles = ['solid', 'glow', 'neon', 'glitch', 'retro', 'cyber', 'outline', 'gradient'];
      const directions = ['0deg', '45deg', '90deg', '135deg', '180deg', '225deg', '270deg', '315deg'];
      
      const randomColor = () => '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
      
      for (const channel of channels) {
        const randFont = fonts[Math.floor(Math.random() * fonts.length)];
        const randStyle = styles[Math.floor(Math.random() * styles.length)];
        const randDir = directions[Math.floor(Math.random() * directions.length)];
        const randC1 = randomColor();
        const randC2 = randomColor();
        const randC3 = randomColor();
        
        const newCust = {
          ...(channel.customization || {}),
          nameFont: randFont,
          font: randFont,
          nameEffect: randStyle,
          nameStyle: randStyle,
          nameEffectColor1: randC1,
          color1: randC1,
          nameEffectColor2: randC2,
          color2: randC2,
          nameColor: randC3,
          gradientDirection: randDir
        };
        
        await supabase
          .from('channels')
          .update({ customization: newCust })
          .eq('id', channel.id);
      }
      
      alert("All channels have been randomized! Go check them out!");
    } catch (err: any) {
      const msg = err?.message || 'Failed to randomize channels';
      setErrorMsg(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  }, [targetType, targetId, onError]);

  const handleReset = useCallback(() => {
    setDraftProfile((prev: any) => ({
      ...prev,
      customization: { ...baselineRef.current },
    }));
    setErrorMsg(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black text-white uppercase tracking-widest">
            {TARGET_LABELS[targetType]} Typography
          </h3>
          <p className="text-[10px] text-white/30 mt-0.5">
            Font, name style, colors, and effects for this {TARGET_LABELS[targetType].toLowerCase()}
          </p>
        </div>
        {dirty && (
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[9px] font-bold text-amber-500 uppercase tracking-widest">
              Unsaved
            </span>
          </div>
        )}
      </div>

      {/* Live Preview */}
      <div className="bg-white/5 rounded-2xl border border-white/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[9px] font-black text-white/30 uppercase tracking-widest">
            Live Preview
          </span>
          <div className="flex-1 h-px bg-white/5" />
        </div>
        {targetType === 'channel' ? (
          <div className="flex flex-col w-64 bg-[#0a0a0a] border border-white/5 rounded-xl p-2 shadow-2xl">
            <div className="px-2 pb-2 pt-1 text-[9px] font-black uppercase tracking-[0.1em] text-white/30">Text Channels</div>
            
            <div className="flex items-center px-2 py-1.5 rounded-md text-[13px] font-medium text-white/40 mb-0.5">
              <Hash size={14} className="mr-2 opacity-40" />
              <span>general</span>
            </div>
            
            <div className="flex items-center px-2 py-1.5 rounded-md text-[13px] font-medium bg-white/10 text-white shadow-[0_0_10px_rgba(255,255,255,0.05)] mb-0.5 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[var(--neon)] rounded-r-full" />
              <Hash size={14} className="mr-2 opacity-80" />
              <span
                style={{
                  fontFamily: FONT_FAMILIES[(draftProfile.customization?.font || draftProfile.customization?.nameFont) as keyof typeof FONT_FAMILIES] || 'inherit',
                  color: draftProfile.customization?.nameColor || '#ffffff',
                  ...getNameStyle(
                    draftProfile.customization?.nameStyle || draftProfile.customization?.nameEffect, 
                    draftProfile.customization?.color1 || draftProfile.customization?.nameEffectColor1, 
                    draftProfile.customization?.color2 || draftProfile.customization?.nameEffectColor2,
                    draftProfile.customization?.gradientDirection
                  )
                }}
              >
                {targetName}
              </span>
            </div>

            <div className="flex items-center px-2 py-1.5 rounded-md text-[13px] font-medium text-white/40 mb-0.5">
              <Hash size={14} className="mr-2 opacity-40" />
              <span>development</span>
            </div>
          </div>
        ) : targetType === 'category' ? (
          <div className="px-2 py-2 flex items-center justify-between group bg-white/5 rounded-xl border border-white/5 w-64">
            <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
              <ChevronDown size={12} className="text-white/30" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] truncate text-white/50">
                <span
                  style={{
                    fontFamily: FONT_FAMILIES[(draftProfile.customization?.font || draftProfile.customization?.nameFont) as keyof typeof FONT_FAMILIES] || 'inherit',
                    color: draftProfile.customization?.nameColor || 'inherit',
                    ...getNameStyle(
                      draftProfile.customization?.nameStyle || draftProfile.customization?.nameEffect, 
                      draftProfile.customization?.color1 || draftProfile.customization?.nameEffectColor1, 
                      draftProfile.customization?.color2 || draftProfile.customization?.nameEffectColor2,
                      draftProfile.customization?.gradientDirection
                    )
                  }}
                >
                  {targetName}
                </span>
              </span>
            </div>
          </div>
        ) : targetType === 'server' ? (
          <div className="px-3 py-3 bg-white/5 rounded-xl border border-white/5 flex items-center justify-between w-64">
            <span className="font-black text-sm truncate pr-2 text-white">
              <span
                style={{
                  fontFamily: FONT_FAMILIES[(draftProfile.customization?.font || draftProfile.customization?.nameFont) as keyof typeof FONT_FAMILIES] || 'inherit',
                  color: draftProfile.customization?.nameColor || '#ffffff',
                  ...getNameStyle(
                    draftProfile.customization?.nameStyle || draftProfile.customization?.nameEffect, 
                    draftProfile.customization?.color1 || draftProfile.customization?.nameEffectColor1, 
                    draftProfile.customization?.color2 || draftProfile.customization?.nameEffectColor2,
                    draftProfile.customization?.gradientDirection
                  )
                }}
              >
                {targetName}
              </span>
            </span>
          </div>
        ) : (
          <ProfilePreview
            profile={draftProfile}
            disableInitialAnimation={true}
          />
        )}
      </div>

      {/* The typography editor (reused from user settings) */}
      <ProfileCustomizer
        draftProfile={draftProfile}
        setDraftProfile={setDraftProfile}
        category="Appearance"
        targetType={targetType}
      />

      {/* Error feedback */}
      {errorMsg && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-[10px] font-bold">
          <span className="text-xs">⚠</span>
          {errorMsg}
          <button onClick={() => setErrorMsg(null)} className="ml-auto text-red-400 hover:text-red-200">✕</button>
        </div>
      )}

      {/* Save/Reset actions */}
      {dirty && (
        <div className="flex items-center gap-3 pt-4 border-t border-white/10">
          <button
            onClick={handleReset}
            className="px-4 py-2.5 rounded-xl text-xs font-bold text-white/50 hover:text-white hover:bg-white/5 transition-colors"
          >
            Reset
          </button>
          <div className="flex-1" />
          {(targetType === 'server' || targetType === 'category') && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleRandomizeChannels}
                disabled={saving}
                title="Randomize typography for all channels"
                className="p-2.5 rounded-xl border border-fuchsia-500/30 text-fuchsia-400 hover:bg-fuchsia-500/10 transition-colors disabled:opacity-40"
              >
                <Sparkles size={16} />
              </button>
              <button
                onClick={handleBulkApply}
                disabled={saving}
                className="px-4 py-2.5 rounded-xl border border-[var(--neon)]/30 text-[var(--neon)] text-xs font-bold hover:bg-[var(--neon)]/10 transition-colors disabled:opacity-40"
              >
                {saving ? 'Applying...' : `Apply to all Channels`}
              </button>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-[var(--neon)] text-black text-xs font-black uppercase tracking-widest hover:shadow-[0_0_15px_var(--neon)] transition-all disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save Typography'}
          </button>
        </div>
      )}
    </div>
  );
}
